import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getEdgeFunctionErrorMessage } from "@/lib/validation";
import { toast } from "sonner";

type ConnectingProvider = "google_calendar" | "zoom" | "calendly" | "disconnect" | null;

export function useCalendar() {
  const { user } = useAuth();
  // Tracks which specific action is in flight so only the button that
  // triggered it shows a spinner — the other two shouldn't look busy too.
  const [connectingProvider, setConnectingProvider] = useState<ConnectingProvider>(null);
  const connecting = connectingProvider !== null;
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [provider, setProvider] = useState<string>("none");
  const [providerLoading, setProviderLoading] = useState(true);

  // Read the calendar provider from user_credentials (single source of truth).
  // RLS restricts it to the owner only.
  useEffect(() => {
    let cancelled = false;
    if (!user) { setProvider("none"); setProviderLoading(false); return; }
    (async () => {
      const { data } = await (supabase as any)
        .from("user_credentials")
        .select("calendar_provider")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      setProvider(data?.calendar_provider ?? "none");
      setProviderLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const startOAuthFlow = useCallback(async (provider: "google_calendar" | "zoom") => {
    if (!user) return;
    setConnectingProvider(provider);
    try {
      const redirectUri = `${window.location.origin}/settings?calendar_callback=${provider}`;
      const { data, error } = await supabase.functions.invoke("calendar-auth", {
        body: { action: "get_auth_url", provider, redirect_uri: redirectUri },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      window.location.href = data.url;
    } catch (err: any) {
      toast.error(err.message || "Failed to start OAuth flow");
      setConnectingProvider(null);
    }
  }, [user]);

  const exchangeCode = useCallback(async (provider: "google_calendar" | "zoom", code: string) => {
    if (!user) return false;
    setConnectingProvider(provider);
    try {
      const redirectUri = `${window.location.origin}/settings?calendar_callback=${provider}`;
      const { data, error } = await supabase.functions.invoke("calendar-auth", {
        body: { action: "exchange_code", provider, code, redirect_uri: redirectUri },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      setProvider(provider);
      toast.success(`${provider === "google_calendar" ? "Google Calendar" : "Zoom"} connected!`);
      return true;
    } catch (err: any) {
      toast.error(err.message || "Failed to connect calendar");
      return false;
    } finally {
      setConnectingProvider(null);
    }
  }, [user]);

  const saveCalendlyKey = useCallback(async (apiKey: string) => {
    if (!user) return;
    setConnectingProvider("calendly");
    try {
      // Validated server-side against Calendly's API before it's ever stored —
      // an unauthenticated upsert would accept any string as a "connection".
      const { data, error } = await supabase.functions.invoke("calendar-auth", {
        body: { action: "connect_calendly", api_key: apiKey },
      });
      if (error || data?.error) {
        throw new Error(data?.error || await getEdgeFunctionErrorMessage(error, "Failed to connect Calendly"));
      }
      // Update local state instead of reloading the page — a full reload
      // wipes the success toast before it's visible.
      setProvider("calendly");
      toast.success("Calendly connected!");
    } finally {
      setConnectingProvider(null);
    }
  }, [user]);

  const disconnect = useCallback(async () => {
    if (!user) return;
    setConnectingProvider("disconnect");
    try {
      const { data, error } = await supabase.functions.invoke("calendar-auth", {
        body: { action: "disconnect" },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      setProvider("none");
      toast.success("Calendar disconnected");
    } catch (err: any) {
      toast.error(err.message || "Failed to disconnect");
    } finally {
      setConnectingProvider(null);
    }
  }, [user]);

  const createEvent = useCallback(async (
    leadName: string,
    leadNotes: string,
    startTime: string,
    durationMinutes = 30,
    opts?: { leadId?: string; attendeeEmail?: string; attendeePhone?: string; location?: string },
  ) => {
    if (!user) return null;
    setCreatingEvent(true);
    try {
      const { data, error } = await supabase.functions.invoke("calendar-create-event", {
        body: {
          lead_name: leadName,
          lead_notes: leadNotes,
          start_time: startTime,
          duration_minutes: durationMinutes,
          lead_id: opts?.leadId,
          attendee_email: opts?.attendeeEmail,
          attendee_phone: opts?.attendeePhone,
          location: opts?.location,
        },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      return data;
    } catch (err: any) {
      toast.error(err.message || "Failed to create event");
      return null;
    } finally {
      setCreatingEvent(false);
    }
  }, [user]);

  const updateEvent = useCallback(async (
    eventId: string,
    leadName: string,
    leadNotes: string,
    startTime: string,
    durationMinutes = 30,
    opts?: { leadId?: string; appointmentId?: string; attendeeEmail?: string; attendeePhone?: string; location?: string },
  ) => {
    if (!user) return null;
    setCreatingEvent(true);
    try {
      const { data, error } = await supabase.functions.invoke("calendar-create-event", {
        body: {
          action: "update",
          event_id: eventId,
          lead_name: leadName,
          lead_notes: leadNotes,
          start_time: startTime,
          duration_minutes: durationMinutes,
          lead_id: opts?.leadId,
          appointment_id: opts?.appointmentId,
          attendee_email: opts?.attendeeEmail,
          attendee_phone: opts?.attendeePhone,
          location: opts?.location,
        },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      return data;
    } catch (err: any) {
      toast.error(err.message || "Failed to update event");
      return null;
    } finally {
      setCreatingEvent(false);
    }
  }, [user]);

  const deleteEvent = useCallback(async (
    eventId: string,
    opts?: { leadId?: string; appointmentId?: string },
  ) => {
    if (!user) return false;
    setCreatingEvent(true);
    try {
      const { data, error } = await supabase.functions.invoke("calendar-create-event", {
        body: {
          action: "delete",
          event_id: eventId,
          lead_id: opts?.leadId,
          appointment_id: opts?.appointmentId,
        },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      return true;
    } catch (err: any) {
      toast.error(err.message || "Failed to delete event");
      return false;
    } finally {
      setCreatingEvent(false);
    }
  }, [user]);

  return {
    startOAuthFlow,
    exchangeCode,
    saveCalendlyKey,
    disconnect,
    createEvent,
    updateEvent,
    deleteEvent,
    connecting,
    connectingProvider,
    creatingEvent,
    provider,
    providerLoading,
  };
}
